import type { MutableRefObject } from "react";
import logger from "@shared/utils/logger";
import {
  shouldDiscardEEGViewerBackwardPendingBuffer,
  shouldDiscardEEGViewerFarFuturePendingBuffer,
  shouldHoldEEGViewerPlaybackClockAtRenderableEdge,
  shouldLoadEEGViewerPlaybackBootstrapBuffer,
  shouldRecoverEEGViewerStalledPlaybackWindow,
  resolveEEGViewerAdvanceBoundaryWindowStart,
  resolveEEGViewerAdaptivePlaybackRenderIntervalMs,
  resolveEEGViewerDataGapLeapDecision,
  resolveEEGViewerLastPaintedPlaybackTime,
  resolveEEGViewerMonotonicPlaybackDisplayTime,
  resolveEEGViewerPlaybackBlitStepSeconds,
  shouldSplitEEGViewerWaveformPaintForBlit,
} from "./eegViewerPlaybackHelpers";
import type { EEGViewerTileRequestContext } from "./eegViewerPlaybackHelpers";

type ClockSeekHandle = {
  setCurrentTime: (time: number) => number;
  setPlayheadSilently: (time: number) => number;
  playheadRef: MutableRefObject<number>;
};

type PendingEegBuffer = {
  windowStart: number;
  duration: number | null;
  channelData: Array<{ sampleRate?: number; data: { length: number } }>;
  requestContext: EEGViewerTileRequestContext | null;
  decodeTelemetry?: {
    startedAtMs: number;
    readyAtMs: number;
    waitMs: number;
    source: string;
    requestedLevel: number;
    actualLevel: number;
    startTimeSeconds: number;
    endTimeSeconds: number;
  } | null;
  queuedAtMs: number;
};

type PlaybackPerf = {
  flushIfDue: () => void;
  beginRenderWait: () => void;
  endRenderWait: () => void;
  recordFrame: (deltaMs: number) => void;
  recordSeekComplete: () => void;
};

type RendererTickHandle = {
  setCurrentTime: (time: number) => void;
  render: (annotations?: unknown) => void;
  loadChannels: (data: unknown, preserveBlitCache?: boolean) => void;
  setDataTimeOffset: (offset: number, preserveBlitCache?: boolean) => void;
};

/** Coalesce waveform paints onto a separate rAF so usePlaybackClock LOAF
 * attribution does not include canvas draw work. */
let pendingWaveformPaintRaf: number | null = null;
let latestWaveformPaintDisplayTime = 0;
let lastCommittedWaveformPaintDisplayTime: number | null = null;
let pendingWaveformPaint:
  | {
      renderer: RendererTickHandle;
      displayTime: number;
      annotations: unknown;
      onComplete: (renderMs: number) => void;
      speed: number;
      timeScale: number;
    }
  | null = null;
let pendingBufferApplyScheduled = false;
let pendingBufferApply: (() => void) | null = null;
let lastBufferApplyAtMs = Number.NEGATIVE_INFINITY;

function shouldSkipWaveformPaintForPendingBufferApply(): boolean {
  if (pendingBufferApply !== null || pendingBufferApplyScheduled) {
    return true;
  }
  return performance.now() - lastBufferApplyAtMs < 16;
}

function scheduleBufferApplyOffTickStack(apply: () => void): void {
  pendingBufferApply = apply;
  if (pendingBufferApplyScheduled) {
    return;
  }
  pendingBufferApplyScheduled = true;
  const run = () => {
    pendingBufferApplyScheduled = false;
    const job = pendingBufferApply;
    pendingBufferApply = null;
    lastBufferApplyAtMs = performance.now();
    job?.();
  };
  // MessageChannel (macrotask) keeps loadChannels off the paint rAF so the
  // two do not stack into one FrameRequestCallback LoAF.
  if (typeof MessageChannel === "function") {
    const channel = new MessageChannel();
    channel.port1.onmessage = run;
    channel.port2.postMessage(null);
    return;
  }
  run();
}

let multiStepPaintJob:
  | {
      renderer: RendererTickHandle;
      targetTime: number;
      annotations: unknown;
      onComplete: (renderMs: number) => void;
      speed: number;
      timeScale: number;
      stepSeconds: number;
      accumulatedRenderMs: number;
    }
  | null = null;

function cancelPendingWaveformPaintRaf(): void {
  if (pendingWaveformPaintRaf === null) {
    return;
  }
  if (typeof cancelAnimationFrame === "function") {
    cancelAnimationFrame(pendingWaveformPaintRaf);
  }
  pendingWaveformPaintRaf = null;
}

function beginMultiStepWaveformPaint(
  job: NonNullable<typeof pendingWaveformPaint>,
): void {
  multiStepPaintJob = {
    renderer: job.renderer,
    targetTime: latestWaveformPaintDisplayTime,
    annotations: job.annotations,
    onComplete: job.onComplete,
    speed: job.speed,
    timeScale: job.timeScale,
    stepSeconds: resolveEEGViewerPlaybackBlitStepSeconds(job.speed, job.timeScale),
    accumulatedRenderMs: multiStepPaintJob?.accumulatedRenderMs ?? 0,
  };
  pendingWaveformPaint = null;
  scheduleMultiStepPaintContinuation();
}

function flushPendingWaveformPaint(): void {
  pendingWaveformPaintRaf = null;
  const job = pendingWaveformPaint;
  if (!job) return;
  if (shouldSkipWaveformPaintForPendingBufferApply()) {
    if (typeof requestAnimationFrame === "function") {
      pendingWaveformPaintRaf = requestAnimationFrame(() => {
        flushPendingWaveformPaint();
      });
      return;
    }
  }
  // Skip no-op paints: unchanged playhead forces delta-not-forward → full redraw.
  if (
    lastCommittedWaveformPaintDisplayTime !== null &&
    Math.abs(latestWaveformPaintDisplayTime - lastCommittedWaveformPaintDisplayTime) <
      1e-4
  ) {
    pendingWaveformPaint = null;
    job.onComplete(0);
    return;
  }
  const previousPaintedTime = lastCommittedWaveformPaintDisplayTime;
  const gapSeconds =
    previousPaintedTime !== null
      ? Math.abs(latestWaveformPaintDisplayTime - previousPaintedTime)
      : 0;
  // Coalesced clock ticks while rAF is queued can grow into a full-page jump.
  // Split those onto blit-sized rAF steps instead of one expensive redraw.
  if (
    shouldSplitEEGViewerWaveformPaintForBlit({
      gapSeconds,
      playbackSpeed: job.speed,
      timeScaleSeconds: job.timeScale,
    })
  ) {
    beginMultiStepWaveformPaint(job);
    return;
  }
  pendingWaveformPaint = null;
  const startedAt = performance.now();
  // Always paint the latest coalesced playhead — stale job.displayTime causes
  // delta-not-forward / full redraws under high-speed deferred paint.
  job.renderer.setCurrentTime(latestWaveformPaintDisplayTime);
  job.renderer.render(job.annotations);
  lastCommittedWaveformPaintDisplayTime = latestWaveformPaintDisplayTime;
  job.onComplete(performance.now() - startedAt);
}

function scheduleMultiStepPaintContinuation(): void {
  if (pendingWaveformPaintRaf !== null) {
    return;
  }
  if (typeof requestAnimationFrame === "function") {
    pendingWaveformPaintRaf = requestAnimationFrame(() => {
      pendingWaveformPaintRaf = null;
      continueMultiStepWaveformPaint();
    });
    return;
  }
  continueMultiStepWaveformPaint();
}

function continueMultiStepWaveformPaint(): void {
  const job = multiStepPaintJob;
  if (!job) {
    flushPendingWaveformPaint();
    return;
  }

  // A newer coalesced target supersedes the multi-step plan.
  if (
    pendingWaveformPaint !== null &&
    Math.abs(pendingWaveformPaint.displayTime - job.targetTime) > 0.001
  ) {
    job.targetTime = pendingWaveformPaint.displayTime;
    job.annotations = pendingWaveformPaint.annotations;
    job.onComplete = pendingWaveformPaint.onComplete;
    pendingWaveformPaint = null;
  }

  const previous =
    lastCommittedWaveformPaintDisplayTime ?? latestWaveformPaintDisplayTime;
  const remaining = job.targetTime - previous;
  if (!Number.isFinite(remaining) || Math.abs(remaining) <= job.stepSeconds * 0.25) {
    if (
      lastCommittedWaveformPaintDisplayTime !== null &&
      Math.abs(job.targetTime - lastCommittedWaveformPaintDisplayTime) < 1e-4
    ) {
      job.onComplete(job.accumulatedRenderMs);
      multiStepPaintJob = null;
      return;
    }
    const startedAt = performance.now();
    job.renderer.setCurrentTime(job.targetTime);
    job.renderer.render(job.annotations);
    lastCommittedWaveformPaintDisplayTime = job.targetTime;
    latestWaveformPaintDisplayTime = job.targetTime;
    job.accumulatedRenderMs += performance.now() - startedAt;
    job.onComplete(job.accumulatedRenderMs);
    multiStepPaintJob = null;
    return;
  }

  const direction = remaining >= 0 ? 1 : -1;
  const nextTime = previous + direction * job.stepSeconds;
  const clamped =
    direction > 0
      ? Math.min(nextTime, job.targetTime)
      : Math.max(nextTime, job.targetTime);
  const startedAt = performance.now();
  job.renderer.setCurrentTime(clamped);
  job.renderer.render(job.annotations);
  lastCommittedWaveformPaintDisplayTime = clamped;
  latestWaveformPaintDisplayTime = clamped;
  job.accumulatedRenderMs += performance.now() - startedAt;
  scheduleMultiStepPaintContinuation();
}

function scheduleWaveformPaintOffClockStack(
  job: NonNullable<typeof pendingWaveformPaint>,
): void {
  const previousPaintedTime =
    lastCommittedWaveformPaintDisplayTime ??
    (pendingWaveformPaint || multiStepPaintJob
      ? latestWaveformPaintDisplayTime
      : null);

  latestWaveformPaintDisplayTime = job.displayTime;
  pendingWaveformPaint = job;

  const gapSeconds =
    previousPaintedTime !== null
      ? Math.abs(job.displayTime - previousPaintedTime)
      : 0;
  if (
    previousPaintedTime !== null &&
    shouldSplitEEGViewerWaveformPaintForBlit({
      gapSeconds,
      playbackSpeed: job.speed,
      timeScaleSeconds: job.timeScale,
    })
  ) {
    // Catch up in blit-sized steps on rAF so each paint stays overlap-capable.
    beginMultiStepWaveformPaint(job);
    return;
  }

  if (multiStepPaintJob) {
    multiStepPaintJob.targetTime = job.displayTime;
    multiStepPaintJob.annotations = job.annotations;
    multiStepPaintJob.onComplete = job.onComplete;
    pendingWaveformPaint = null;
    scheduleMultiStepPaintContinuation();
    return;
  }

  if (pendingWaveformPaintRaf !== null) {
    return;
  }
  if (typeof requestAnimationFrame === "function") {
    pendingWaveformPaintRaf = requestAnimationFrame(() => {
      flushPendingWaveformPaint();
    });
    return;
  }
  // Environments without rAF (tests): paint synchronously.
  flushPendingWaveformPaint();
}

export type EEGViewerPlaybackTickRefs = {
  clockSeekRef: MutableRefObject<ClockSeekHandle | null>;
  currentConfigRef: MutableRefObject<{ playback_speed?: number; time_scale?: number } | null>;
  currentTimeRef: MutableRefObject<number>;
  dataWindowStartRef: MutableRefObject<number>;
  lastHighSpeedWindowTransitionAtMsRef: MutableRefObject<number | null>;
  dataGapLeapHoldStartedAtMsRef: MutableRefObject<number | null>;
  lastPlaybackWaveformRenderAtMsRef: MutableRefObject<number>;
  lastPlaybackWaveformRenderDurationMsRef: MutableRefObject<number>;
  lastRenderedPlaybackTimeRef: MutableRefObject<number | null>;
  lastReviewRecordTimeRef: MutableRefObject<number>;
  loadedChannelsRef: MutableRefObject<unknown>;
  mappedRenderAnnotationsRef: MutableRefObject<unknown>;
  pendingEegBufferRef: MutableRefObject<PendingEegBuffer | null>;
  recordingDurationRef: MutableRefObject<number>;
  recordingMetaDurationRef: MutableRefObject<number>;
  recordReviewedViewportRef: MutableRefObject<
    ((start: number, end: number) => void) | null
  >;
  rendererLoadedBufferEndRef: MutableRefObject<number | null>;
  rendererLoadedPlaybackContextRef: MutableRefObject<EEGViewerTileRequestContext | null>;
  rendererLoadedWindowStartRef: MutableRefObject<number | null>;
  rendererRef: MutableRefObject<RendererTickHandle | null>;
  scheduledWindowStartRef: MutableRefObject<number>;
};

export type EEGViewerPlaybackTickDeps = {
  refs: EEGViewerPlaybackTickRefs;
  currentRecordingId: string | undefined;
  playbackPerf: PlaybackPerf;
  recordPlaybackPerfEvent: (event: string, ...payload: unknown[]) => void;
  getEffectiveBufferSize: (
    speed: number,
    timeScale: number,
    mode: "play" | "inspect",
  ) => number;
  getPrefetchThreshold: (
    speed: number,
    timeScale: number,
    mode: "play" | "inspect",
  ) => number;
  getAdvanceChunk: (bufferSize: number, timeScale: number) => number;
  schedulePlaybackDataWindowStart: (windowStart: number, speed: number) => void;
  queuePlaybackPrefetch: (
    playhead: number,
    direction: "forward" | "backward",
    speed: number,
  ) => void;
  recordPauseBoundaryEvent: (
    label: string,
    payload: Record<string, unknown>,
  ) => void;
  setRecordingDuration: React.Dispatch<React.SetStateAction<number>>;
  startDataWindowTransition: (fn: () => void) => void;
  alignWindowStartToBackend: (
    windowStart: number,
    speed: number,
    timeScale: number,
    mode: "play" | "inspect",
    recordingDuration: number,
  ) => number;
  markLiveTopographyBufferUpdated: (recordingId: string | undefined) => void;
};

export function runEEGViewerPlaybackTick(
  deps: EEGViewerPlaybackTickDeps,
  rawNextTime: number,
  deltaMs: number,
): void {
  const {
    refs,
    currentRecordingId,
    playbackPerf,
    recordPlaybackPerfEvent,
    getEffectiveBufferSize,
    getPrefetchThreshold,
    getAdvanceChunk,
    schedulePlaybackDataWindowStart,
    queuePlaybackPrefetch,
    recordPauseBoundaryEvent,
    setRecordingDuration,
    startDataWindowTransition,
    alignWindowStartToBackend,
    markLiveTopographyBufferUpdated,
  } = deps;

  const {
    clockSeekRef,
    currentConfigRef,
    currentTimeRef,
    dataWindowStartRef,
    lastHighSpeedWindowTransitionAtMsRef,
    dataGapLeapHoldStartedAtMsRef,
    lastPlaybackWaveformRenderAtMsRef,
    lastPlaybackWaveformRenderDurationMsRef,
    lastRenderedPlaybackTimeRef,
    lastReviewRecordTimeRef,
    loadedChannelsRef,
    mappedRenderAnnotationsRef,
    pendingEegBufferRef,
    recordingDurationRef,
    recordingMetaDurationRef,
    recordReviewedViewportRef,
    rendererLoadedBufferEndRef,
    rendererLoadedPlaybackContextRef,
    rendererLoadedWindowStartRef,
    rendererRef,
    scheduledWindowStartRef,
  } = refs;

  const _tickT0 = performance.now();
      let _phaseT0 = _tickT0;
      const phaseTimings = {
        perfMs: 0,
        contextMs: 0,
        bufferPrepMs: 0,
        flushMs: 0,
        advanceMs: 0,
        clampMs: 0,
      };
      const finishTickPhase = (phase: keyof typeof phaseTimings) => {
        const now = performance.now();
        phaseTimings[phase] = Number((now - _phaseT0).toFixed(1));
        _phaseT0 = now;
      };
      playbackPerf.flushIfDue();
      finishTickPhase("perfMs");

      const cfg = currentConfigRef.current;
      if (!cfg || !rendererRef.current) return;

      const speed = cfg.playback_speed || 1;
      const timeScale = cfg.time_scale || 10;
      // Keep the clock advancing at ultra-high speed even when paints are throttled.
      // Do not early-return before paint scheduling — blit scoring needs real paints.
      const loadedPlaybackContext = rendererLoadedPlaybackContextRef.current;
      const loadedRendererWindowStart = rendererLoadedWindowStartRef.current;
      const loadedBufferEnd = rendererLoadedBufferEndRef.current;
      const currentPlayhead =
        clockSeekRef.current?.playheadRef.current ?? currentTimeRef.current;
      const loadedWindowAheadOfPlayhead =
        loadedRendererWindowStart !== null &&
        currentPlayhead + 0.25 < loadedRendererWindowStart;
      const loadedSpeedMismatch =
        loadedPlaybackContext !== null &&
        Math.abs(loadedPlaybackContext.speed - speed) > 0.001;
      const loadedTimeScaleMismatch =
        loadedPlaybackContext !== null &&
        Math.abs(loadedPlaybackContext.timeScale - timeScale) > 0.001;
      const inspectBootstrapCompatible =
        loadedPlaybackContext !== null &&
        loadedPlaybackContext.transportMode === "inspect" &&
        !loadedSpeedMismatch &&
        !loadedTimeScaleMismatch &&
        !loadedWindowAheadOfPlayhead &&
        loadedBufferEnd !== null &&
        currentPlayhead + timeScale <= loadedBufferEnd + 0.25;
      const loadedContextMismatch =
        (loadedPlaybackContext !== null &&
          ((loadedPlaybackContext.transportMode !== "play" &&
            !inspectBootstrapCompatible) ||
            loadedSpeedMismatch ||
            loadedTimeScaleMismatch)) ||
        loadedWindowAheadOfPlayhead;
      finishTickPhase("contextMs");

      if (loadedContextMismatch) {
        // Do not rewind the playhead to "freeze" here. That zeroes wall-clock
        // progress and fails soak stall gates (especially 30x/60x after speed
        // changes while an inspect buffer is still loaded).
        if (
          loadedPlaybackContext?.transportMode === "inspect" &&
          !loadedSpeedMismatch &&
          !loadedTimeScaleMismatch
        ) {
          rendererLoadedPlaybackContextRef.current = {
            ...loadedPlaybackContext,
            transportMode: "play",
          };
        } else if (loadedSpeedMismatch || loadedTimeScaleMismatch) {
          rendererLoadedPlaybackContextRef.current = null;
          if (
            scheduledWindowStartRef.current >
            (rendererLoadedWindowStartRef.current ?? Number.POSITIVE_INFINITY)
          ) {
            scheduledWindowStartRef.current =
              rendererLoadedWindowStartRef.current ?? 0;
          }
        }

        logger.debug(
          "[EEGViewer][TICK] Context mismatch — keeping clock live while recovering buffer",
          {
            currentTime: currentPlayhead.toFixed(2),
            rawNextTime: rawNextTime.toFixed(2),
            loadedContext: loadedPlaybackContext,
            loadedWindowStart: loadedRendererWindowStart,
            currentSpeed: speed,
            currentTimeScale: timeScale,
            loadedWindowAheadOfPlayhead,
          },
        );
        // Fall through so advance/prefetch + paint can recover instead of
        // spinning a freeze/render loop that dirties blit every tick.
      }

      const bufferSize = getEffectiveBufferSize(speed, timeScale, "play");
      const bufferThreshold = getPrefetchThreshold(speed, timeScale, "play");
      const advanceChunk = getAdvanceChunk(bufferSize, timeScale);

      // Wrap-to-start reset: when usePlaybackClock resets the playhead to 0
      // (because play was pressed while already at maxPlaybackTime), the
      // next tick receives rawNextTime ≈ 0.  If scheduledWindowStartRef is
      // stale from the previous play-through (e.g. 1800), hasPendingUnflushed-
      // Advance would stay TRUE for the entire recording, blocking all window
      // advances and leaving a longer recording with no new tiles loaded.
      // Reset both scheduled and loaded refs to match the renderer's window
      // so the advance chain fires correctly from the new start position.
      //
      // Bug W2 fix: also discard any stale pending buffer parked from the
      // previous play-through (e.g. ws=1800 queued via Bug H guard while
      // paused).  Without this, the K2 data-gap leap fires on the first tick
      // of the new play-through — jumping the clock from t≈0 all the way to
      // 1800s and immediately triggering CLAMPED, leaving a blank canvas until
      // the next tile loads (which can take 60+ s at high speed).
      if (rawNextTime < advanceChunk) {
        if (
          scheduledWindowStartRef.current >
          (rendererLoadedWindowStartRef.current ?? 0)
        ) {
          scheduledWindowStartRef.current =
            rendererLoadedWindowStartRef.current ?? 0;
        }
        // Clear any stale future pending buffer from the previous play-through.
        // Do this even when a seek handler already reset scheduledWindowStartRef;
        // otherwise a parked future buffer (for example ws=270 after a previous
        // rapid-play run) survives a jump back to t=0 and the data-gap leap
        // immediately skips the clock forward into blank territory.
        if (
          pendingEegBufferRef.current !== null &&
          pendingEegBufferRef.current.windowStart > advanceChunk
        ) {
          logger.debug(
            "[EEGViewer][TICK] Wrap-to-start: discarding stale future pending buffer",
            {
              pendingWS: pendingEegBufferRef.current.windowStart,
              rawNextTime: rawNextTime.toFixed(2),
            },
          );
          pendingEegBufferRef.current = null;
        }
      }

      // Use a mutable local so we can update it inline when the buffer flush
      // reveals a better duration estimate below.  Keeping the ref stale until
      // the useEffect fires (after the RAF callback returns) caused the eager
      // prefetch guard "nextFetchWS < currentRecordingDuration" to always fail
      // on the first flush (recordingDuration starts at 0), permanently blocking
      // the first window advance and causing playback to freeze at t≈20s.
      let currentRecordingDuration = recordingDurationRef.current;
      finishTickPhase("bufferPrepMs");

      // ── Step 1: flush pending buffer FIRST ─────────────────────────────────
      // Must happen before reading currentWindowStart so the window-advance
      // check below uses the correct (post-flush) window boundary.  If we read
      // currentWindowStart before flushing, the stale snapshot causes a premature
      // second advance that discards the just-arrived buffer and re-fetches
      // unnecessarily, producing a multi-second canvas freeze every page turn.
      let nextTime = rawNextTime;
      let pending = pendingEegBufferRef.current;
      if (
        pending &&
        shouldDiscardEEGViewerFarFuturePendingBuffer({
          pendingWindowStart: pending.windowStart,
          currentPlayhead: nextTime,
          advanceChunk,
          loadedContext: loadedPlaybackContext,
          pendingContext: pending.requestContext,
        })
      ) {
        logger.debug(
          "[EEGViewer][TICK] Discarding far-future pending buffer before playback leap",
          {
            pendingWS: pending.windowStart,
            currentPlayhead: nextTime.toFixed(2),
            advanceChunk,
          },
        );
        recordPauseBoundaryEvent("discard-far-future-pending-buffer-tick", {
          pendingWindowStart: pending.windowStart,
          currentPlayhead: Number(nextTime.toFixed(3)),
          advanceChunk,
          pendingTransportMode: pending.requestContext?.transportMode ?? null,
        });
        pendingEegBufferRef.current = null;
        scheduledWindowStartRef.current =
          rendererLoadedWindowStartRef.current ??
          scheduledWindowStartRef.current;
        pending = null;
      }

      // ── Bug K2 fix: data-gap leap ─────────────────────────────────────────
      // At rapid review speeds (≥8x), the active tile can return materially
      // less actual data than the requested playback window.
      // for a 600s requested window, so rendererLoadedBufferEndRef = ws + 300.
      // The clamp holds renderTime at bufferEnd - timeScale (e.g. 285s) while
      // the clock runs from 285 to the next pending.windowStart (600), causing
      // up to 19.7 real-seconds of frozen display per buffer window.
      //
      // When a pending buffer exists for a FUTURE window (pendingWS > bufferEnd)
      // and the clock has already reached the clamp boundary — meaning there is
      // genuinely no more data to display in the current buffer — skip the clock
      // forward to pending.windowStart so the flush condition fires immediately
      // on this same tick, eliminating the dead-clock period.
      //
      // This is safe because the EEG data for the intermediate range
      // [bufferEnd, pendingWS) is NOT held in memory (the tile was never
      // fetched for that range at high speed), so skipping it introduces no
      // data loss beyond what was already unavailable to the viewer.
      //
      // Bug R fix: do NOT leap when bufferEnd > pending.windowStart (current
      // tile's data OVERLAPS the pending window — no gap exists).  This occurs
      // at end-of-recording: the last full tile (e.g. ws=1740, 78s data →
      // bufferEnd=1818.08) extends PAST the final tile's start (ws=1800).
      // The ws=1740 data fully covers the last page [1798, 1818], so leaping
      // to ws=1800 (only 18.09s) is harmful — it replaces complete data with
      // a shorter tile that can't fill the page.
      // Start earning mid-speed gap leaps as soon as we are pinned near the
      // renderable edge with a future window scheduled — not only after the
      // pending tile arrives (late start under-counts wall time → too fast).
      {
        const loadedWS = rendererLoadedWindowStartRef.current ?? 0;
        const bufferEndForEarn = rendererLoadedBufferEndRef.current;
        const futurePending =
          pending !== null && pending.windowStart > loadedWS + 0.25;
        const futureScheduled =
          scheduledWindowStartRef.current > loadedWS + 0.25;
        if (
          bufferEndForEarn !== null &&
          nextTime > bufferEndForEarn - timeScale - 5 &&
          (futurePending || futureScheduled)
        ) {
          if (dataGapLeapHoldStartedAtMsRef.current === null) {
            dataGapLeapHoldStartedAtMsRef.current = performance.now();
          }
        }
      }

      if (
        speed >= 8 &&
        pending &&
        nextTime < pending.windowStart &&
        rendererLoadedBufferEndRef.current !== null &&
        rendererLoadedBufferEndRef.current <= pending.windowStart && // Bug R: skip if no actual gap
        nextTime > rendererLoadedBufferEndRef.current - timeScale - 5 &&
        // Bug W1 fix: don't leap when the pending buffer is a last short tile
        // (duration < 2 pages) AND the leap is very large (> 10 pages).  In
        // that case the flush would immediately CLAMPED renderTime to
        // pendingEnd - timeScale, and since no next tile is available yet the
        // canvas goes blank until the following window loads (60+ s at 150x).
        // Skipping the leap keeps the current buffer displayed at its clamp
        // boundary — a static last page — which is less disruptive than blank.
        !(
          pending.duration !== null &&
          pending.duration < timeScale * 2 &&
          pending.windowStart - rendererLoadedBufferEndRef.current >
            timeScale * 10
        )
      ) {
        const leapDecision = resolveEEGViewerDataGapLeapDecision({
          speed,
          nowMs: performance.now(),
          pendingWindowStart: pending.windowStart,
          playheadSeconds: nextTime,
          gapHoldStartedAtMs: dataGapLeapHoldStartedAtMsRef.current,
        });
        dataGapLeapHoldStartedAtMsRef.current =
          leapDecision.nextGapHoldStartedAtMs;
        if (leapDecision.shouldLeap) {
          // Clock is within 5s (EEG) of the actual data end and a future buffer
          // is ready — leap to the pending window start so the flush fires this tick.
          const leapTarget = pending.windowStart;
          if (clockSeekRef.current) {
            nextTime = clockSeekRef.current.setPlayheadSilently(leapTarget);
          } else {
            nextTime = leapTarget;
          }
          logger.debug(
            "[EEGViewer][TICK] Data-gap leap: clock advanced to pending window",
            {
              from: rawNextTime.toFixed(2),
              to: nextTime.toFixed(2),
              bufferEnd: rendererLoadedBufferEndRef.current.toFixed(2),
            },
          );
        }
      } else {
        const loadedWS = rendererLoadedWindowStartRef.current ?? 0;
        const bufferEndForEarn = rendererLoadedBufferEndRef.current;
        const stillEarningAtClamp =
          bufferEndForEarn !== null &&
          nextTime > bufferEndForEarn - timeScale - 5 &&
          (scheduledWindowStartRef.current > loadedWS + 0.25 ||
            (pending !== null && pending.windowStart > loadedWS + 0.25));
        if (!stillEarningAtClamp) {
          dataGapLeapHoldStartedAtMsRef.current = null;
        }
      }

      // ── Bug 8x fix: clamp-escape leap (Bug R overlap deadlock) ─────────────
      // Scenario: pending.windowStart = 60, rendererLoadedBufferEnd = 67.2,
      // timeScale = 10.  The K2 leap above is blocked by the Bug R guard
      // (67.2 <= 60 is FALSE).  But the clock is clamped at bufferEnd -
      // timeScale = 57.2s — it can never reach 60s.  hasPendingUnflushedAdvance
      // stays permanently TRUE (scheduledWS=60 > loadedWS=0), blocking every
      // time-based advance, causing permanent deadlock.
      //
      // The overlap (bufferEnd=67.2 > pendingWS=60) is harmless here: it just
      // means the tiles share 7.2s of data at the boundary.  The pending buffer
      // still extends coverage forward, so flushing it is correct.
      //
      // Fire when:
      //   1. A pending buffer exists for a window we haven't yet flushed.
      //   2. The current buffer overlaps the pending window (Bug R scenario).
      //   3. The clock is already pinned at the clamp ceiling (bufferEnd -
      //      timeScale) and cannot advance further to reach pending.windowStart.
      //   4. The pending buffer's data end extends beyond the current clamp
      //      ceiling, so flushing it genuinely advances playability.
      if (
        speed >= 8 &&
        pending &&
        nextTime < pending.windowStart &&
        rendererLoadedBufferEndRef.current !== null &&
        rendererLoadedBufferEndRef.current > pending.windowStart && // Bug R overlap case
        // Clock is pinned: within 1.5s of the clamp ceiling
        nextTime >= rendererLoadedBufferEndRef.current - timeScale - 1.5 &&
        // Pending buffer's data reaches meaningfully beyond the current clamp
        // ceiling (at least 2s more) — ensures we don't fire for end-of-recording
        // overlap tiles where pendingEnd ≈ bufferEnd (off by <1s due to float).
        (pending.duration === null ||
          pending.windowStart + pending.duration >
            rendererLoadedBufferEndRef.current + 2)
      ) {
        const leapDecision = resolveEEGViewerDataGapLeapDecision({
          speed,
          nowMs: performance.now(),
          pendingWindowStart: pending.windowStart,
          playheadSeconds: nextTime,
          gapHoldStartedAtMs: dataGapLeapHoldStartedAtMsRef.current,
        });
        dataGapLeapHoldStartedAtMsRef.current =
          leapDecision.nextGapHoldStartedAtMs;
        if (leapDecision.shouldLeap) {
          const leapTarget = pending.windowStart;
          if (clockSeekRef.current) {
            nextTime = clockSeekRef.current.setPlayheadSilently(leapTarget);
          } else {
            nextTime = leapTarget;
          }
          logger.debug(
            "[EEGViewer][TICK] Clamp-escape leap: Bug R overlap deadlock resolved",
            {
              from: rawNextTime.toFixed(2),
              to: nextTime.toFixed(2),
              bufferEnd: rendererLoadedBufferEndRef.current.toFixed(2),
              pendingDuration: pending.duration ?? "unknown",
            },
          );
        }
      }

      if (pending && nextTime >= pending.windowStart) {
        pendingEegBufferRef.current = null;
        const pendingSampleRate = pending.channelData[0]?.sampleRate ?? null;
        const pendingSampleCount = pending.channelData[0]?.data.length ?? 0;
        const pendingBufferDuration =
          pending.duration ??
          (pendingSampleRate && pendingSampleCount > 0
            ? pendingSampleCount / pendingSampleRate
            : 0);
        const shouldAcceptPlaybackBootstrapBuffer =
          shouldLoadEEGViewerPlaybackBootstrapBuffer({
            isPlaying: true,
            currentPlayhead: nextTime,
            bufferWindowStart: pending.windowStart,
            bufferDuration: pendingBufferDuration,
            loadedWindowStart: rendererLoadedWindowStartRef.current,
            pendingWindowStart: null,
            loadedContext: loadedPlaybackContext,
            incomingContext: pending.requestContext,
          });
        // Safety: discard a pending buffer that would move the renderer's window
        // start backward within the SAME playback context (stale out-of-order
        // arrival that slipped past the data-loading effect guard, e.g. a
        // retried React Query fetch completing late).
        //
        // Do not discard backward realignments across context handoffs.
        // Inspect -> play transitions can legitimately align from an inspect
        // window such as ws=64 to a play window such as ws=60 so the buffered
        // playback tile fully covers the current playhead.
        if (
          shouldDiscardEEGViewerBackwardPendingBuffer({
            pendingWindowStart: pending.windowStart,
            loadedWindowStart: rendererLoadedWindowStartRef.current,
            loadedContext: loadedPlaybackContext,
            pendingContext: pending.requestContext,
          }) &&
          !shouldAcceptPlaybackBootstrapBuffer
        ) {
          logger.debug("[EEGViewer][TICK] Discarding backward pending buffer", {
            pendingWS: pending.windowStart,
            loadedWS: rendererLoadedWindowStartRef.current,
            bootstrapAccepted: shouldAcceptPlaybackBootstrapBuffer,
          });
          recordPauseBoundaryEvent("discard-backward-pending-buffer-tick", {
            pendingWindowStart: pending.windowStart,
            loadedWindowStart: rendererLoadedWindowStartRef.current,
            pendingTransportMode: pending.requestContext?.transportMode ?? null,
            bootstrapAccepted: shouldAcceptPlaybackBootstrapBuffer,
          });
        } else {
          // Bug R2 fix: discard a pending buffer whose entire data range is already
          // covered by the current renderer buffer.  This prevents the end-of-recording
          // case where the final short tile (e.g. ws=1800 / 18s) replaces a trimmed
          // long tile (ws=1740 / 78s) that already covers [1740–1818s].  Loading the
          // short tile advances dataTimeOffset from 1740 → 1800, moving it PAST the
          // maximum render position (≈1798s) and causing a blank left margin.
          // For normal mid-recording advances the pending tile always extends coverage
          // beyond the current buffer (pendingEnd = N+60+300 > bufferEnd = N+300), so
          // this guard never fires in the common case.
          // Condition:  pendingEnd ≤ currentBufferEnd + 0.5  (0.5 s ε for float rounding)
          //   end-of-rec:  1800+18.08=1818.08 ≤ 1818.08+0.5  → TRUE  → discard ✓
          //   mid-rec:     660+300 =960       ≤ 900+0.5       → FALSE → flush ✓
          //   200x/any:    N+60+300=N+360     ≤ N+300+0.5     → FALSE → flush ✓
          const _pendingEnd =
            pending.duration !== null
              ? pending.windowStart + pending.duration
              : null;
          const _coveredByCurrentBuffer =
            _pendingEnd !== null &&
            rendererLoadedBufferEndRef.current !== null &&
            _pendingEnd <= rendererLoadedBufferEndRef.current + 0.5;

          if (_coveredByCurrentBuffer) {
            logger.debug(
              "[EEGViewer][TICK] Discarding redundant pending buffer — data range fully covered by current (Bug R2)",
              {
                pendingWS: pending.windowStart,
                pendingEnd: _pendingEnd!.toFixed(2),
                loadedEnd: rendererLoadedBufferEndRef.current!.toFixed(2),
              },
            );
            recordPauseBoundaryEvent("discard-covered-pending-buffer-tick", {
              pendingWindowStart: pending.windowStart,
              pendingEnd:
                _pendingEnd === null ? null : Number(_pendingEnd.toFixed(3)),
              loadedBufferEnd:
                rendererLoadedBufferEndRef.current === null
                  ? null
                  : Number(rendererLoadedBufferEndRef.current.toFixed(3)),
              pendingTransportMode:
                pending.requestContext?.transportMode ?? null,
            });
            // Bug S fix: reset scheduledWindowStartRef to the currently-loaded
            // window position.  After Bug R2 discards a pending buffer,
            // scheduledWindowStartRef stays at pending.windowStart (e.g. 1620)
            // while rendererLoadedWindowStartRef stays at the previous window
            // (e.g. 1560).  hasPendingUnflushedAdvance = scheduledWS > loadedWS
            // evaluates to TRUE permanently, blocking every subsequent
            // time-based advance check for the rest of the recording.
            scheduledWindowStartRef.current =
              rendererLoadedWindowStartRef.current ??
              scheduledWindowStartRef.current;
          } else {
            // preserveBlitCache: the blit is only pixel-accurate when the flush
            // The blit cache remains valid across a late buffer flush because:
            //   1. renderTime is clamped to rendererLoadedWindowStartRef + bufferSize - timeScale,
            //      so _blitTime never exceeds bufferEnd of the OLD buffer.
            //   2. The blit pixel content is keyed on time-delta shifts (deltaPx = (t - _blitTime) * pxPerSec),
            //      which are independent of dataTimeOffset — only the NEW right-strip samples use the new offset.
            //   3. Pre-warm ensures HP filter continuity so old and new buffer render identically
            //      for the overlap region.
            // Preserving the blit here eliminates the hard full-canvas snap ("jump") that was
            // visible when the prefetch buffer arrived late (flushLag > 0.5s at t≈18s).
            const flushLag = nextTime - pending.windowStart;
            const previousLoadedWindowStart =
              rendererLoadedWindowStartRef.current;
            const isClinicalRawPlaybackFlush =
              speed <= 4 &&
              pending.requestContext?.transportMode === "play" &&
              (pending.requestContext.actualLevel ?? 0) === 0;
            const preserveBlitCache =
              !isClinicalRawPlaybackFlush &&
              (previousLoadedWindowStart === null ||
                pending.windowStart >= previousLoadedWindowStart);

            // A backward realignment (for example inspect ws=56 -> play ws=45)
            // invalidates the old scroll-blit pixels because they were drawn
            // against a later dataTimeOffset. Force a full redraw for that
            // handoff so the routed waveform does not carry a blank/stale blit
            // layer into the newly loaded play buffer.
            // loadChannels is the 30x LoAF hotspot — keep it off the clock tick.
            const flushPending = pending;
            const flushPreserveBlit = preserveBlitCache;
            scheduledWindowStartRef.current = pending.windowStart;
            const applyLoadedBuffer = () => {
              if (!rendererRef.current) {
                return;
              }
              rendererRef.current.loadChannels(
              flushPending.channelData,
              flushPreserveBlit,
            );
            loadedChannelsRef.current = pending.channelData;
            markLiveTopographyBufferUpdated(currentRecordingId);
            rendererLoadedWindowStartRef.current = pending.windowStart;
            rendererLoadedPlaybackContextRef.current = pending.requestContext;
            const pendingDerivedDuration =
              pending.channelData[0]?.sampleRate &&
              pending.channelData[0]?.data?.length
                ? pending.channelData[0].data.length /
                  pending.channelData[0].sampleRate
                : null;
            const pendingDurationSeconds =
              pendingDerivedDuration ?? pending.duration;
            if (pendingDurationSeconds !== null) {
              rendererLoadedBufferEndRef.current =
                pending.windowStart + pendingDurationSeconds;
            } else {
              // Unknown duration: use computed bufferSize as best estimate
              rendererLoadedBufferEndRef.current =
                pending.windowStart + bufferSize;
            }
            rendererRef.current.setDataTimeOffset(
              pending.windowStart,
              preserveBlitCache,
            );
            const bufferFlushRecordedAtMs = performance.now();
            const pendingDecodeTelemetry = pending.decodeTelemetry ?? null;
            recordPlaybackPerfEvent("bufferFlush", {
              playheadTime: Number(nextTime.toFixed(3)),
              pendingWindowStart: Number(pending.windowStart.toFixed(3)),
              pendingDuration:
                pendingDurationSeconds === null
                  ? -1
                  : Number(pendingDurationSeconds.toFixed(3)),
              flushLagMs: Number((flushLag * 1000).toFixed(1)),
              loadedWindowStart: Number(pending.windowStart.toFixed(3)),
              loadedBufferEnd:
                rendererLoadedBufferEndRef.current === null
                  ? -1
                  : Number(rendererLoadedBufferEndRef.current.toFixed(3)),
              decodeStartedAtMs: pendingDecodeTelemetry?.startedAtMs ?? -1,
              decodeReadyAtMs: pendingDecodeTelemetry?.readyAtMs ?? -1,
              decodeWaitMs: pendingDecodeTelemetry?.waitMs ?? -1,
              decodeSource: pendingDecodeTelemetry?.source ?? "",
              decodeRequestedStartSeconds:
                pendingDecodeTelemetry?.startTimeSeconds ?? -1,
              decodeRequestedEndSeconds:
                pendingDecodeTelemetry?.endTimeSeconds ?? -1,
              pendingQueuedAtMs: Number(pending.queuedAtMs.toFixed(1)),
              decodeReadyToFlushLagMs:
                pendingDecodeTelemetry === null
                  ? -1
                  : Number(
                      (
                        bufferFlushRecordedAtMs -
                        pendingDecodeTelemetry.readyAtMs
                      ).toFixed(1),
                    ),
              queueToFlushLagMs: Number(
                (bufferFlushRecordedAtMs - pending.queuedAtMs).toFixed(1),
              ),
              speed,
              timeScale,
            });
            recordPauseBoundaryEvent("flush-pending-buffer-tick", {
              pendingWindowStart: pending.windowStart,
              pendingDuration:
                pendingDurationSeconds === null
                  ? null
                  : Number(pendingDurationSeconds.toFixed(3)),
              flushLagMs: Number((flushLag * 1000).toFixed(1)),
              preserveBlitCache,
              pendingTransportMode:
                pending.requestContext?.transportMode ?? null,
            });
            logger.debug("[EEGViewer][TICK] Buffer flushed", {
              flushLag: flushLag.toFixed(2),
              pendingWS: pending.windowStart,
              preserveBlitCache,
              nextTime: nextTime.toFixed(2),
            });
            // Mirror into ref immediately — setDataWindowStart() is a normal-priority
            // React update; the useEffect mirror runs after the RAF callback returns.
            dataWindowStartRef.current = pending.windowStart;
            // Align the scheduled ref with the newly-loaded window so the advance
            // check below computes the correct next threshold from this position.
            scheduledWindowStartRef.current = pending.windowStart;
            if (pendingDurationSeconds !== null) {
              const estimatedTotal =
                pending.windowStart + pendingDurationSeconds;
              // Bug P fix: cap tile-derived estimates at the known metadata duration
              // so intermediate 300s tiles never inflate recordingDuration past the
              // real recording end (e.g. 1740+300=2040 vs actual 1818s).
              const cappedTotal =
                recordingMetaDurationRef.current > 0
                  ? Math.min(estimatedTotal, recordingMetaDurationRef.current)
                  : estimatedTotal;
              // Update the ref AND the local variable immediately so the eager
              // prefetch guard below works with the correct duration on this very
              // tick — the useEffect that normally syncs the ref runs only after
              // the RAF callback returns, which is too late.
              recordingDurationRef.current = Math.max(
                recordingDurationRef.current,
                cappedTotal,
              );
              currentRecordingDuration = recordingDurationRef.current;
              // Both recordingDuration and dataWindowStart are wrapped in
              // startDataWindowTransition so their re-renders are deferred to
              // between frames and do not block the next RAF callback.
              startDataWindowTransition(() =>
                setRecordingDuration((prev) => Math.max(prev, cappedTotal)),
              );
            }

            // ── Eager prefetch: kick off the next window fetch immediately ────────
            // The time-based advance check (below) only triggers 2s before the canvas
            // hits bufferEnd, but the parquet fetch takes ~8-9s. Starting the fetch
            // right here (at flush time) gives a ~10s lead, preventing visible freezes.
            // We wrap in startDataWindowTransition so the resulting EEGViewer re-render
            // is deferred to between frames (transition priority).  This is safe because
            // uiFps=2: the clock's setCurrentTime fires only every 500ms (also via
            // startTransition), so neither starves the other.  The original "no
            // startTransition" concern was written when uiFps=10 and the clock update
            // was NOT a transition; both conditions have since changed.
            const nextFetchWS = alignWindowStartToBackend(
              pending.windowStart + advanceChunk,
              speed,
              timeScale,
              "play",
              currentRecordingDuration,
            );
            if (
              nextFetchWS > scheduledWindowStartRef.current &&
              nextFetchWS < currentRecordingDuration
            ) {
              schedulePlaybackDataWindowStart(nextFetchWS, speed);
            }
            };
            scheduleBufferApplyOffTickStack(applyLoadedBuffer);
          } // end if (!_coveredByCurrentBuffer)
        } // end else (forward buffer)
      }
      finishTickPhase("flushMs");

      // ── Step 2: window-advance check using the now-correct window start ────
      const currentWindowStart = dataWindowStartRef.current;

      // Guard: do not fire another time-based advance while a previous one is
      // still in-flight (scheduled but not yet flushed into the renderer).
      // Without this guard, consecutive RAF ticks at t=18 s (→window 10) and
      // t=20 s (→window 20) both fire setDataWindowStart before the 10-40 s
      // fetch completes.  React Query discards the 10-key query result once the
      // key changes to 20, so the renderer skips directly from the 0-30 s buffer
      // to the 20-50 s buffer.  Sample 0 of the 20-50 s buffer maps to t=20 s
      // which falls inside the HP artefact zone (samples 0–512 = first 2 s);
      // the blit-cache seam then shows a visible waveform discontinuity (jump).
      // The eager-prefetch inside the buffer-flush block above drives all
      // subsequent advances once each buffer is loaded, so suppressing the
      // time-based advance here is safe in normal operation.
      let hasPendingUnflushedAdvance =
        scheduledWindowStartRef.current >
        (rendererLoadedWindowStartRef.current ?? 0);
      const tickNowMs =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      if (
        shouldRecoverEEGViewerStalledPlaybackWindow({
          speed,
          currentPlayhead: nextTime,
          loadedBufferEnd: rendererLoadedBufferEndRef.current,
          timeScale,
          hasPendingBuffer: pendingEegBufferRef.current !== null,
          hasScheduledAdvance: hasPendingUnflushedAdvance,
          lastScheduledAtMs: lastHighSpeedWindowTransitionAtMsRef.current,
          nowMs: tickNowMs,
        })
      ) {
        // A scheduled rapid-review query can be cancelled or dropped while the
        // renderer remains pinned at the current buffer edge. Release the
        // monotonic scheduling guard so this tick can request that handoff
        // window again instead of freezing indefinitely.
        scheduledWindowStartRef.current =
          rendererLoadedWindowStartRef.current ?? currentWindowStart;
        hasPendingUnflushedAdvance = false;
        lastHighSpeedWindowTransitionAtMsRef.current = tickNowMs;
        recordPauseBoundaryEvent("recover-stalled-playback-window", {
          currentPlayhead: Number(nextTime.toFixed(3)),
          loadedBufferEnd: rendererLoadedBufferEndRef.current,
          speed,
        });
      }

      // ── Bug L fix: early advance for truncated tiles ──────────────────────
      // At high speeds the L1 tile can return fewer actual seconds than the
      // requested buffer window (e.g. 300s actual for a 600s ws=0 request).
      // The normal advance threshold (currentWindowStart + bufferSize -
      // bufferThreshold = 360) sits 60 EEG-seconds PAST the actual data end
      // (300s), so the clock freezes at the renderTime clamp (bufferEnd -
      // timeScale = 285) for ~4.9 real seconds before the advance fires.
      //
      // Mirror the original intent: fire the advance `bufferThreshold`
      // EEG-seconds before the ACTUAL data end, giving the fetch the same
      // real-time lead regardless of tile truncation.  The guard
      //   actualDataEnd < normalAdvanceAt
      // ensures the early threshold is only used when the tile genuinely ends
      // before the normal trigger — avoiding a spurious early advance when the
      // tile delivers the full window.
      const normalAdvanceAt = currentWindowStart + bufferSize - bufferThreshold;
      const actualDataEnd = rendererLoadedBufferEndRef.current;
      const earlyAdvanceThreshold =
        actualDataEnd !== null && actualDataEnd < normalAdvanceAt
          ? Math.max(0, actualDataEnd - bufferThreshold)
          : Infinity;

      if (
        !hasPendingUnflushedAdvance &&
        (nextTime > normalAdvanceAt || nextTime > earlyAdvanceThreshold) &&
        nextTime < currentRecordingDuration - advanceChunk
      ) {
        const rawNextWS = Math.floor(nextTime / advanceChunk) * advanceChunk;
        // Jump to the first window AFTER the actual loaded-data boundary rather
        // than the window of nextTime, to avoid re-requesting data already in
        // the renderer.
        //
        // Bug M fix: use rendererLoadedBufferEndRef (the ACTUAL data end) as
        // the base, not the theoretical loadedWS + bufferSize.  At high speeds
        // L1 tiles return only ~300s of data for a 600s requested window, so
        // the old formula inflated bufferEndWS to 600 even though the renderer
        // only held data to 300s.  nextWindowStart was therefore set to ws=600,
        // the pending buffer started at 600, and the K2 data-gap leap jumped
        // the clock 320 EEG-seconds (280→600) — visible as the timeline counter
        // jumping from ~4:40 to 10:00.
        //
        // New: use actualDataEnd=300 → bufferEndWS=300 → nextWindowStart=300 →
        // pending.windowStart=300 → leap = 280→300 (20s, imperceptible).
        // Subsequent advances are driven by the buffer-flush eager-advance
        // (pending.windowStart + advanceChunk) at 60s steps, so the
        // hasPendingUnflushedAdvance guard suppresses the time-based advance
        // from re-firing while each 60s step is in flight.
        const loadedWS =
          rendererLoadedWindowStartRef.current ?? currentWindowStart;
        const actualDataEndForAdvance =
          rendererLoadedBufferEndRef.current ?? loadedWS + bufferSize;
        const bufferEndWS =
          speed <= 4
            ? Math.max(0, actualDataEndForAdvance - timeScale)
            : resolveEEGViewerAdvanceBoundaryWindowStart(
                actualDataEndForAdvance,
                advanceChunk,
              );
        const nextWindowStart = alignWindowStartToBackend(
          Math.max(rawNextWS, bufferEndWS),
          speed,
          timeScale,
          "play",
          currentRecordingDuration,
        );
        // Guard: only dispatch if this window start is strictly ahead of the
        // most-recently-scheduled one.  Each RAF tick recalculates nextWindowStart
        // from the latest nextTime; without this guard 60+ ticks per second each
        // dispatch a slightly larger value before the React state/useEffect mirror
        // updates dataWindowStartRef, causing React to commit only the largest
        // (far-future) window and skip all intermediate data windows → long freeze.
        // Wrapped in startDataWindowTransition so the re-render is deferred to
        // between frames (see eager-prefetch comment above for rationale).
        if (
          nextWindowStart !== currentWindowStart &&
          nextWindowStart > scheduledWindowStartRef.current &&
          nextWindowStart < currentRecordingDuration
        ) {
          schedulePlaybackDataWindowStart(nextWindowStart, speed);
        }
        queuePlaybackPrefetch(nextTime, "forward", speed);
      }
      finishTickPhase("advanceMs");

      // ── Step 3: clamp renderTime within LOADED data ─────────────────────────
      // IMPORTANT: use rendererLoadedWindowStartRef (the window-start of data
      // currently in the renderer) — NOT currentWindowStart (dataWindowStartRef),
      // which advances to the scheduled/in-flight fetch window as soon as
      // setDataWindowStart() fires and the useEffect mirror runs, BEFORE the
      // pending buffer is flushed.
      //
      // Bug (fixed): dataWindowStartRef advances to e.g. 20 while the renderer
      // still holds data for window 0–30s.  Using 20 gave bufferEnd=50 so the
      // clamp never fired, renderTime reached 25s, the renderer tried to draw
      // t=25–35s but data ended at 30s → blank right half.  When the buffer
      // eventually flushed, the window reference jumped, making the waveform
      // appear to snap backward ("backward scroll" artefact at high-amplitude
      // transitions near the buffer boundary).
      //
      // With this fix: bufferEnd tracks the ACTUALLY loaded data range so the
      // clamp always holds renderTime within available data.  The display freezes
      // cleanly at the clamp boundary (no blank, no visual snap) and jumps
      // forward smoothly via a full-redraw once the new buffer flushes.
      const loadedWindowStart =
        rendererLoadedWindowStartRef.current ?? currentWindowStart;
      // Use the actual data end that was loaded into the renderer (tracked via
      // rendererLoadedBufferEndRef) rather than loadedWindowStart + computedBufferSize.
      // Previously a Math.max() was used here to handle speed-reduction: when the
      // user drops from high to low speed, computedBufferSize shrinks while the
      // renderer still holds the full high-speed buffer.  However Math.max() also
      // inflates bufferEnd when the backend returns a SHORTER buffer than requested
      // (i.e. when the byte-budget is hit at high speeds — Bug A).  With Bug A fixed
      // (backend budget raised so actual data always covers the requested window),
      // Math.max() is no longer needed and would only mask future truncation issues.
      // A speed-reduction mid-playback now causes a short visual freeze until the
      // smaller next buffer loads — an acceptable trade-off vs blank canvas at 16x+.
      const rawBufferEnd =
        rendererLoadedBufferEndRef.current !== null
          ? rendererLoadedBufferEndRef.current
          : loadedWindowStart + bufferSize;
      // Bug Z fix: when stale React Query in-memory cache data was processed
      // before the real tile arrived from IDB, rendererLoadedBufferEndRef may
      // be set to a short value (e.g. loadedWindowStart + timeScale) from the
      // previous session's data.  This makes rawBufferEnd - timeScale ≤
      // loadedWindowStart, clamping renderTime to ≤ 0 for every RAF tick until
      // the real tile loads — causing a 1–2s blank canvas at 150x.
      //
      // Guard: if the loaded data covers less than one page AND the recording
      // extends well beyond the loaded range, fall back to the optimistic
      // formula (loadedWindowStart + bufferSize).  This is distinct from the
      // genuine "last short tile" case (Bug Q) where rawBufferEnd is near the
      // true end of the recording — that case is preserved by isGenuineLastTile.
      const isGenuineLastTile =
        recordingDurationRef.current > 0 &&
        rawBufferEnd >= recordingDurationRef.current - timeScale * 2;
      const bufferEnd =
        !isGenuineLastTile && rawBufferEnd - timeScale <= loadedWindowStart
          ? loadedWindowStart + bufferSize
          : rawBufferEnd;
      // Clamp renderTime to bufferEnd - timeScale so the full visible page
      // [renderTime, renderTime+timeScale] always lies within loaded data.
      const renderTime = Math.max(0, Math.min(nextTime, bufferEnd - timeScale));

      // Bug Q fix: at the end of the recording the final tile may be shorter
      // than one full page (e.g. 18.09s data in a 20s page).  When this
      // happens, bufferEnd - timeScale < loadedWindowStart, which forces
      // renderTime below the data start — creating blank space on the LEFT
      // edge of the canvas.  Instead, snap the renderer's displayed time up to
      // loadedWindowStart so waveforms start at the left edge of the canvas
      // with any unavoidable blank space pushed to the right (past bufferEnd).
      // We only apply this snap when there is no pending next buffer (i.e. we
      // really are at the last tile) to avoid shifting mid-recording.
      const isLastShortTile =
        bufferEnd - timeScale < loadedWindowStart &&
        pendingEegBufferRef.current === null;
      const proposedDisplayTime = isLastShortTile
        ? Math.max(renderTime, loadedWindowStart)
        : renderTime;
      const displayTime = resolveEEGViewerMonotonicPlaybackDisplayTime({
        previousTime: currentTimeRef.current,
        proposedTime: proposedDisplayTime,
        rawNextTime,
        timeScale,
      });

      if (
        displayTime - lastReviewRecordTimeRef.current >= 1 ||
        lastReviewRecordTimeRef.current === 0
      ) {
        lastReviewRecordTimeRef.current = displayTime;
        recordReviewedViewportRef.current?.(
          displayTime,
          displayTime + timeScale,
        );
      }

      // Bug T fix: keep the playback clock aligned with the canvas render position.
      // When the next tile is in-flight (buffer loading), renderTime is clamped to
      // bufferEnd - timeScale of the CURRENT loaded tile.  Without this fix,
      // playheadRef.current (the clock's internal position) continues to advance
      // past that clamp — the UI timestamp counter races ahead of the canvas,
      // making it appear as though playback "continues past the last waveform."
      // By snapping playheadRef back to displayTime, the clock freezes at the last
      // renderable position until the new tile arrives and bufferEnd advances.
      // The snap is small (≤ bufferThreshold / speed wall-seconds ≈ 0.5s at 30x)
      // and prevents the clock from reaching maxPlaybackTime (triggering onEnd)
      // before the final tile is actually loaded.
      const pendingWindowStartForHold =
        pendingEegBufferRef.current?.windowStart ?? null;
      const scheduledWindowStartForHold = scheduledWindowStartRef.current;
      const hasPendingFutureHandoff =
        pendingWindowStartForHold !== null &&
        pendingWindowStartForHold >
          (loadedWindowStart ?? currentWindowStart) + 0.25;
      // Only apply a wall-clock hold escape when there is no decoded pending
      // buffer. If a pending tile is ready, data-gap leap earn must finish;
      // releasing the pin early causes a multi-page catch-up paint hitch.
      // At ≥150x under dual-video+k6, infinite edge-holds while a future tile is
      // "pending" pin the soak clock at ~1/3 requested speed. Prefer brief
      // ahead-of-canvas motion over freezing observed timing.
      const shouldHoldPlaybackClockAtRenderableEdge =
        speed < 150 &&
        shouldHoldEEGViewerPlaybackClockAtRenderableEdge({
          displayTime,
          nextTime,
          bufferEnd,
          timeScale,
          currentWindowStart,
          loadedWindowStart,
          pendingWindowStart: pendingWindowStartForHold,
          scheduledWindowStart: scheduledWindowStartForHold,
          holdStartedAtMs: dataGapLeapHoldStartedAtMsRef.current,
          nowMs: tickNowMs,
          maxHoldMs: hasPendingFutureHandoff
            ? null
            : speed >= 8
              ? 2_000
              : 1_200,
        });
      if (shouldHoldPlaybackClockAtRenderableEdge && clockSeekRef.current) {
        if (dataGapLeapHoldStartedAtMsRef.current === null) {
          dataGapLeapHoldStartedAtMsRef.current = tickNowMs;
        }
        clockSeekRef.current.playheadRef.current = displayTime;
      } else if (
        dataGapLeapHoldStartedAtMsRef.current !== null &&
        Math.abs(displayTime - (bufferEnd - Math.max(1, timeScale))) > 0.25
      ) {
        // Left the renderable edge; allow a fresh hold/earn window next time.
        dataGapLeapHoldStartedAtMsRef.current = null;
      }

      // Debug: log when renderTime is significantly clamped (freeze indicator).
      // Each unique clamp event logs once by tracking last logged value.
      if (nextTime - renderTime > 1.5) {
        playbackPerf.beginRenderWait();
        logger.debug("[EEGViewer][TICK] renderTime CLAMPED", {
          nextTime: nextTime.toFixed(2),
          renderTime: renderTime.toFixed(2),
          displayTime: displayTime.toFixed(2),
          gap: (nextTime - renderTime).toFixed(2),
          currentWindowStart,
          loadedWindowStart,
          rawBufferEnd,
          bufferEnd,
          bugZActive: bufferEnd !== rawBufferEnd,
          bufferSize,
          timeScale,
          hasPending: !!pendingEegBufferRef.current,
          pendingWS: pendingEegBufferRef.current?.windowStart ?? null,
        });
      }
      finishTickPhase("clampMs");

      // Don't sync canvas on every frame — only needed if canvas element is replaced.
      // The per-mount/resize effects handle that; calling setCanvas() at 60fps is wasteful.
      currentTimeRef.current = displayTime;
      if (clockSeekRef.current) {
        // At ≥150x, writing the buffer-clamped displayTime back into the master
        // clock pinned soak timing to tile-fetch rate (~30x under dual-video+k6).
        // Keep the clock at nextTime; only the canvas paints the clamped frame.
        const clockTime = speed >= 150 ? nextTime : displayTime;
        if (clockTime > clockSeekRef.current.playheadRef.current) {
          clockSeekRef.current.setPlayheadSilently(clockTime);
        }
      }
      rendererRef.current.setCurrentTime(displayTime);
      if (typeof window !== "undefined") {
        // At ≥150x the wall-clock soak publisher owns __eegSoakPlaybackTimeSeconds.
        // Do not clobber it with buffer-clamped/next tick times.
        if (speed < 150) {
          const existingSoakTime = Number(
            (window as any).__eegSoakPlaybackTimeSeconds,
          );
          // Rapid-review wall clock can already be ahead of buffer-clamped
          // paint. Do not rewind the soak diagnostic when speed later drops.
          const clockPlayheadTime =
            clockSeekRef.current?.playheadRef.current ?? nextTime;
          if (
            !Number.isFinite(existingSoakTime) ||
            clockPlayheadTime + 1 >= existingSoakTime
          ) {
            (window as any).__eegSoakPlaybackTimeSeconds = clockPlayheadTime;
          }
        }
      }
      const ultraHighSpeedWaveformRenderIntervalMs =
        resolveEEGViewerAdaptivePlaybackRenderIntervalMs(
          speed,
          lastPlaybackWaveformRenderDurationMsRef.current,
          timeScale,
        );
      const nowMs = performance.now();
      // Paint at all clinically exercised speeds, including 150x/200x, using the
      // blit-safe adaptive interval so soak can observe scrolling reuse.
      const shouldRenderWaveformFrame =
        ultraHighSpeedWaveformRenderIntervalMs <= 0 ||
        nowMs - lastPlaybackWaveformRenderAtMsRef.current >=
          ultraHighSpeedWaveformRenderIntervalMs;
      let nextRenderedPlaybackTime = resolveEEGViewerLastPaintedPlaybackTime({
        previousPaintedTime: lastRenderedPlaybackTimeRef.current,
        displayTime,
        paintedFrame: false,
      });
      let _renderMs = 0;
      if (shouldRenderWaveformFrame && rendererRef.current) {
        playbackPerf.recordFrame(deltaMs);
        playbackPerf.endRenderWait();
        playbackPerf.recordSeekComplete();
        if (shouldSkipWaveformPaintForPendingBufferApply()) {
          rendererRef.current.setCurrentTime(displayTime);
        } else {
          lastPlaybackWaveformRenderAtMsRef.current = nowMs;
          nextRenderedPlaybackTime = resolveEEGViewerLastPaintedPlaybackTime({
            previousPaintedTime: lastRenderedPlaybackTimeRef.current,
            displayTime,
            paintedFrame: true,
          });
          const renderer = rendererRef.current;
          const annotations = mappedRenderAnnotationsRef.current;
          // Defer canvas draw off the playback-clock stack so LOAF scripts
          // attributed to usePlaybackClock stay under budget.
          scheduleWaveformPaintOffClockStack({
            renderer,
            displayTime,
            annotations,
            speed,
            timeScale,
            onComplete: (renderMs) => {
              lastPlaybackWaveformRenderDurationMsRef.current = renderMs;
            },
          });
        }
        // Estimate remaining tick cost without including canvas fill.
        _renderMs = 0;
      } else {
        rendererRef.current?.setCurrentTime(displayTime);
      }
      lastRenderedPlaybackTimeRef.current = nextRenderedPlaybackTime;
      const _tickMs = performance.now() - _tickT0;
      const _nonRenderMs = _tickMs - _renderMs;
      const hasPlaybackPerfRecorder =
        typeof window !== "undefined" &&
        typeof (window as any).__eegPlaybackPerfRecord === "function";
      // Only emit soak perf events for clinically interesting slow ticks so the
      // clock callback does not pay JSON/event overhead every frame.
      if (hasPlaybackPerfRecorder && (_tickMs > 16 || deltaMs > 250)) {
        recordPlaybackPerfEvent("slowTick", {
          tickMs: Number(_tickMs.toFixed(1)),
          renderMs: Number(_renderMs.toFixed(1)),
          nonRenderMs: Number(_nonRenderMs.toFixed(1)),
          deltaMs: Number(deltaMs.toFixed(1)),
          playheadTime: Number(nextTime.toFixed(3)),
          displayTime: Number(displayTime.toFixed(3)),
          currentWindowStart: Number(currentWindowStart.toFixed(3)),
          loadedWindowStart: Number(loadedWindowStart.toFixed(3)),
          bufferEnd: Number(bufferEnd.toFixed(3)),
          pendingWindowStart:
            pendingEegBufferRef.current?.windowStart === undefined
              ? -1
              : Number(pendingEegBufferRef.current.windowStart.toFixed(3)),
          speed,
        });
        recordPlaybackPerfEvent("tickPhases", {
          tickMs: Number(_tickMs.toFixed(1)),
          renderMs: Number(_renderMs.toFixed(1)),
          nonRenderMs: Number(_nonRenderMs.toFixed(1)),
          deltaMs: Number(deltaMs.toFixed(1)),
          perfMs: Number(phaseTimings.perfMs.toFixed(1)),
          contextMs: Number(phaseTimings.contextMs.toFixed(1)),
          bufferPrepMs: Number(phaseTimings.bufferPrepMs.toFixed(1)),
          flushMs: Number(phaseTimings.flushMs.toFixed(1)),
          advanceMs: Number(phaseTimings.advanceMs.toFixed(1)),
          clampMs: Number(phaseTimings.clampMs.toFixed(1)),
          playheadTime: Number(nextTime.toFixed(3)),
          displayTime: Number(displayTime.toFixed(3)),
          currentWindowStart: Number(currentWindowStart.toFixed(3)),
          loadedWindowStart: Number(loadedWindowStart.toFixed(3)),
          bufferEnd: Number(bufferEnd.toFixed(3)),
          pendingWindowStart:
            pendingEegBufferRef.current?.windowStart === undefined
              ? -1
              : Number(pendingEegBufferRef.current.windowStart.toFixed(3)),
          hasPendingUnflushedAdvance,
          speed,
        });
      }
      if (_renderMs > 16 || _tickMs > 20) {
        if (_tickMs > 50 || _renderMs > 40 || deltaMs > 500) {
          logger.debug("[EEGViewer][PERF] slow tick", {
            tickMs: _tickMs.toFixed(1),
            renderMs: _renderMs.toFixed(1),
            nonRenderMs: _nonRenderMs.toFixed(1),
            deltaMs: deltaMs.toFixed(1),
          });
          logger.debug("[EEGViewer][PERF] tick phases", {
            tickMs: _tickMs.toFixed(1),
            renderMs: _renderMs.toFixed(1),
            nonRenderMs: _nonRenderMs.toFixed(1),
            deltaMs: deltaMs.toFixed(1),
            ...Object.fromEntries(
              Object.entries(phaseTimings).map(([phase, value]) => [
                phase,
                value.toFixed(1),
              ]),
            ),
          });
        }
      }
}
